/* eslint-disable camelcase */
import PublicEndpoint from './public-endpoint'
import SecureEndpoint from './secure-endpoint'
import { createQueryParams, createRandomString, removeTrailingSlash, encode, sha256, decode } from '../utils/helpers'
import { CookieStorage, Storage } from '../utils/storage'

export const DEFAULT_AUTH0_CLIENT = {
  name: 'auth0-spreadthemusic-js',
  version: '1.2.0',
}

const DEFAULT_REDIRECT_URI = 'http://localhost/authorized'

const DEFAULT_SESSION_CHECK_EXPIRY_MS = 24 * 3600

const CODE_VERIFIER_KEY = 'oauth.spreadthemusic.codeverifier'
const AUTH_CODE_KEY = 'oauth.spreadthemusic.authcode'
const ACCESS_TOKEN_KEY = 'oauth.spreadthemusic.access_token'
const REFRESH_TOKEN_KEY = 'oauth.spreadthemusic.refresh_token'
const MFA_TOKEN_KEY = 'oauth.spreadthemusic.mfa_token'

const generateState = () => {
  return createRandomString(64)
}

const generateNonce = () => {
  return createRandomString(64)
}

type BaseFlowOptions = {
  client_id: string
  organization?: string
  audience?: string
  prompt?: 'none'
  [key: string]: unknown
}

type AuthorizeFlowOptions = BaseFlowOptions & {
  response_type: string
  response_mode: string
  redirect_uri: string
  nonce: string
  state: string
  scope: string
  code_challenge: string
  code_challenge_method: string
}

type TokenFlowOptions = BaseFlowOptions & {
  client_id: string
  grant_type: string
  code: string
  redirect_uri: string
  code_verifier?: string
  state: string
}

type AuthenticatorsFlowOptions = BaseFlowOptions & {
  client_id: string
  code_verifier: string
  state: string
}

type LogoutFlowOptions = BaseFlowOptions & {
  client_id: string
  code_verifier: string
  state: string
}

type RegisterFlowOptions = BaseFlowOptions & {
  client_id: string
  code_verifier: string
  state: string
}

type ForgotFlowOptions = BaseFlowOptions & {
  client_id: string
  code_verifier: string
  state: string
  email: string
}

type AssociateFlowOptions = BaseFlowOptions & {
  authenticator_types: string
  oob_channels: string
  phone_number: string
  client_id: string
  state: string
}

class OAuthService {
  private storage: Storage = CookieStorage

  // eslint-disable-next-line no-useless-constructor
  constructor(private clientid: string) {}

  private getOAuthUrls(
    options: {
      authorizeUrl?: string
      authenticatorsUrl?: string
      associateUrl?: string
      issuer?: string
      userinfoUrl?: string
      tokenUrl?: string
      logoutUrl?: string
      forgotUrl?: string
      revokeUrl?: string
      registerUrl?: string
    } = {
      issuer: '',
    },
  ) {
    // Get user-supplied arguments
    let authorizeUrl = removeTrailingSlash(options.authorizeUrl)
    let authenticatorsUrl = removeTrailingSlash(options.authenticatorsUrl)
    let associateUrl = removeTrailingSlash(options.associateUrl)
    const issuer = removeTrailingSlash(options.issuer)
    let userinfoUrl = removeTrailingSlash(options.userinfoUrl)
    let tokenUrl = removeTrailingSlash(options.tokenUrl)
    let logoutUrl = removeTrailingSlash(options.logoutUrl)
    let forgotUrl = removeTrailingSlash(options.forgotUrl)
    let revokeUrl = removeTrailingSlash(options.revokeUrl)
    let registerUrl = removeTrailingSlash(options.registerUrl)

    let baseUrl = issuer.indexOf('/api') > 0 ? issuer : issuer + '/api'

    baseUrl = baseUrl.indexOf('/v1') > 0 ? baseUrl : baseUrl + '/v1'

    baseUrl = baseUrl.indexOf('/oauth2') > 0 ? baseUrl : baseUrl + '/oauth2'

    authorizeUrl = authorizeUrl || baseUrl + '/authorize'
    userinfoUrl = userinfoUrl || baseUrl + '/userinfo'
    associateUrl = associateUrl || baseUrl + '/associate'
    authenticatorsUrl = authenticatorsUrl || baseUrl + '/authenticators'
    tokenUrl = tokenUrl || baseUrl + '/token'
    revokeUrl = revokeUrl || baseUrl + '/revoke'
    logoutUrl = logoutUrl || baseUrl + '/logout'
    forgotUrl = forgotUrl || baseUrl + '/forgot'
    registerUrl = registerUrl || baseUrl + '/register'

    return {
      issuer: issuer,
      authorizeUrl: authorizeUrl,
      associateUrl: associateUrl,
      authenticatorsUrl: authenticatorsUrl,
      userinfoUrl: userinfoUrl,
      tokenUrl: tokenUrl,
      revokeUrl: revokeUrl,
      logoutUrl: logoutUrl,
      forgotUrl: forgotUrl,
      registerUrl: registerUrl,
    }
  }

  private async buildAuthorizeParams(codeVerifier: string): Promise<AuthorizeFlowOptions> {
    const flowOptions: AuthorizeFlowOptions = {
      state: encode(generateState()),
      nonce: encode(generateNonce()),
      response_type: 'code',
      response_mode: 'query',
      scope: 'openid profile email',
      redirect_uri: DEFAULT_REDIRECT_URI,
      code_challenge: await sha256(codeVerifier),
      code_challenge_method: 'S256',
      client_id: this.clientid,
    }

    return flowOptions
  }

  private getCodeVerifier(): string {
    return this.storage.get(CODE_VERIFIER_KEY) || createRandomString()
  }

  private storeCodeVerifier(codeVerifier: string): void {
    this.storage.set(CODE_VERIFIER_KEY, codeVerifier, {
      expireIn: DEFAULT_SESSION_CHECK_EXPIRY_MS,
    })
  }

  private clearCodeVerifier(): string {
    return this.storage.unset(CODE_VERIFIER_KEY)
  }

  private getAuthCode(): string {
    return this.storage.get(AUTH_CODE_KEY)
  }

  private clearAuthCode(): string {
    return this.storage.unset(AUTH_CODE_KEY)
  }

  private storeAuthCode(authCode: string): void {
    this.storage.set(AUTH_CODE_KEY, authCode, {
      expireIn: DEFAULT_SESSION_CHECK_EXPIRY_MS,
    })
  }

  public getAccessToken(): string {
    return this.storage.get(ACCESS_TOKEN_KEY)
  }

  private clearAccessToken(): string {
    return this.storage.unset(ACCESS_TOKEN_KEY)
  }

  public hasAccessToken(): boolean {
    return this.storage.get(ACCESS_TOKEN_KEY) !== undefined
  }

  private storeAccessToken(token: string): void {
    this.storage.set(ACCESS_TOKEN_KEY, token, {
      expireIn: DEFAULT_SESSION_CHECK_EXPIRY_MS,
    })
  }

  private getRefreshToken(): string {
    return this.storage.get(REFRESH_TOKEN_KEY)
  }

  public hasRefreshToken(): boolean {
    return !!this.storage.get(REFRESH_TOKEN_KEY)
  }

  private storeRefreshToken(token: string): void {
    this.storage.set(REFRESH_TOKEN_KEY, token, {
      expireIn: 24 * 3600,
    })
  }

  private clearRefreshToken(): string {
    return this.storage.unset(REFRESH_TOKEN_KEY)
  }

  private getMfaToken(): string {
    return this.storage.get(MFA_TOKEN_KEY)
  }

  private storeMfaToken(token: string): void {
    this.storage.set(MFA_TOKEN_KEY, token, {
      expireIn: DEFAULT_SESSION_CHECK_EXPIRY_MS,
    })
  }

  private clearMfaToken(): string {
    return this.storage.unset(MFA_TOKEN_KEY)
  }

  private async buildTokenParams(authCode: string, grantType = 'authorization_code') {
    const codeVerifier = this.getCodeVerifier()

    const flowOptions: TokenFlowOptions = {
      grant_type: grantType,
      code: authCode,
      redirect_uri: DEFAULT_REDIRECT_URI,
      code_verifier: encode(codeVerifier),
      client_id: this.clientid,
      state: encode(generateState()),
    }

    return flowOptions
  }

  private async buildAuthenticatorsParams(): Promise<AuthenticatorsFlowOptions> {
    const codeVerifier = this.getCodeVerifier()

    const flowOptions: AuthenticatorsFlowOptions = {
      code_verifier: encode(codeVerifier),
      client_id: this.clientid,
      state: encode(generateState()),
    }

    return flowOptions
  }

  private async buildRegisterParams(): Promise<RegisterFlowOptions> {
    const codeVerifier = this.getCodeVerifier()

    const flowOptions: RegisterFlowOptions = {
      code_verifier: encode(codeVerifier),
      client_id: this.clientid,
      state: encode(generateState()),
    }

    return flowOptions
  }

  private async buildForgotParams(email: string): Promise<ForgotFlowOptions> {
    const codeVerifier = this.getCodeVerifier()

    const flowOptions: ForgotFlowOptions = {
      code_verifier: encode(codeVerifier),
      client_id: this.clientid,
      state: encode(generateState()),
      email: email,
    }

    return flowOptions
  }

  private async buildLogoutParams(): Promise<LogoutFlowOptions> {
    const codeVerifier = this.getCodeVerifier()

    const flowOptions: AuthenticatorsFlowOptions = {
      code_verifier: encode(codeVerifier),
      client_id: this.clientid,
      state: encode(generateState()),
    }

    return flowOptions
  }

  private async buildAssociateParams(phoneNumber: string): Promise<AssociateFlowOptions> {
    // const codeVerifier = this.getCodeVerifier()

    const flowOptions: AssociateFlowOptions = {
      authenticator_types: 'oob',
      oob_channels: 'sms',
      phone_number: phoneNumber,
      client_id: this.clientid,
      state: encode(generateState()),
    }

    return flowOptions
  }

  public async authorize(credentials: { username: string; password: string }): Promise<any> {
    const urls = this.getOAuthUrls()

    const codeVerifier = this.getCodeVerifier()
    this.storeCodeVerifier(codeVerifier)

    const params = await this.buildAuthorizeParams(codeVerifier)

    const authorization = 'Basic ' + btoa(credentials.username + ':' + credentials.password)

    return PublicEndpoint()
      .post(
        urls.authorizeUrl,
        {
          password: credentials.password,
          email: credentials.username,
          ...params,
        },
        {
          headers: {
            Authorization: authorization,
          },
        },
      )
      .then((response) => {
        if (response.data.success) {
          if (decode(response.data.payload.state) !== decode(params.state)) {
            return Promise.reject(new Error('Invalid state.'))
          } else {
            this.storeAuthCode(response.data.payload.code)
          }
        }
        return response.data
      })
  }

  public async token(binding_code?: string): Promise<any> {
    const urls = this.getOAuthUrls()
    const authCode = this.getAuthCode()

    const params = await this.buildTokenParams(authCode)
    if (binding_code) {
      params.grant_type = 'mfa_code'
    }

    return PublicEndpoint()
      .post(urls.tokenUrl, {
        binding_code: binding_code,
        mfa_token: this.getMfaToken(),
        ...params,
      })
      .then((response) => {
        if (response.data.success) {
          if (decode(response.data.payload.state) !== decode(params.state)) {
            return Promise.reject(new Error('Invalid state.'))
          } else {
            if (response.data.payload.status === 'E108') {
              // MFA Required
              this.clearAuthCode()
              this.storeMfaToken(response.data.payload.mfa_token)
            } else {
              this.clearMfaToken()
              this.clearAuthCode()
              this.storeAccessToken(response.data.payload.access_token)
              this.storeRefreshToken(response.data.payload.refresh_token)
            }
          }
        }
        return response.data
      })
  }

  public async authenticators(): Promise<any> {
    const urls = this.getOAuthUrls()
    const token = this.getMfaToken()
    const params = await this.buildAuthenticatorsParams()

    const authorization = 'Bearer ' + token
    const url = `${urls.authenticatorsUrl}?${createQueryParams(params)}`
    return PublicEndpoint()
      .get(url, {
        headers: {
          Authorization: authorization,
        },
      })
      .then((response) => {
        if (response.data.success) {
          if (decode(response.data.payload.state) !== decode(params.state)) {
            return Promise.reject(new Error('Invalid state.'))
          }
        }
        return response.data
      })
  }

  public async associate(mobilePhone: string): Promise<any> {
    const urls = this.getOAuthUrls()
    const token = this.getMfaToken()
    const params = await this.buildAssociateParams(mobilePhone)
    const authorization = 'Bearer ' + token

    return PublicEndpoint()
      .post(
        urls.associateUrl,
        {
          ...params,
        },
        {
          headers: {
            Authorization: authorization,
          },
        },
      )
      .then((response) => {
        if (response.data.success) {
          if (decode(response.data.payload.state) !== decode(params.state)) {
            return Promise.reject(new Error('Invalid state.'))
          }
        }
        return response.data
      })
  }

  public async logout(): Promise<any> {
    console.debug('Logout!')
    const urls = this.getOAuthUrls()
    const params = await this.buildLogoutParams()
    const token = this.getAccessToken()
    const authorization = 'Bearer ' + token

    return PublicEndpoint()
      .post(
        urls.logoutUrl,
        {
          ...params,
        },
        {
          headers: {
            Authorization: authorization,
          },
        },
      )
      .then((response) => {
        if (response.data.success) {
          if (decode(response.data.payload.state) !== decode(params.state)) {
            return Promise.reject(new Error('Invalid state.'))
          }
        }
        this.clearAuthCode()
        this.clearAccessToken()
        this.clearRefreshToken()
        this.clearMfaToken()
        this.clearCodeVerifier()
        return response.data
      })
  }

  public async forgot(email: string): Promise<any> {
    const urls = this.getOAuthUrls()
    const params = await this.buildForgotParams(email)
    return PublicEndpoint()
      .post(urls.forgotUrl, {
        ...params,
      })
      .then((response) => {
        if (response.data.success) {
          if (decode(response.data.payload.state) !== decode(params.state)) {
            return Promise.reject(new Error('Invalid state.'))
          }
        }
        this.clearAuthCode()
        this.clearAccessToken()
        this.clearRefreshToken()
        this.clearMfaToken()
        this.clearCodeVerifier()
        return response.data
      })
  }

  public async register(credentials: { username: string; password: string; passwordCheck: string }): Promise<any> {
    const urls = this.getOAuthUrls()
    const params = await this.buildRegisterParams()

    return PublicEndpoint()
      .post(urls.registerUrl, {
        password: credentials.password,
        email: credentials.username,
        ...params,
      })
      .then((response) => {
        if (response.data.success) {
          if (decode(response.data.payload.state) !== decode(params.state)) {
            return Promise.reject(new Error('Invalid state.'))
          }
        }
        return response.data
      })
  }

  public async qrCode(): Promise<any> {
    //const urls = this.getOAuthUrls()

    const codeVerifier = this.getCodeVerifier()
    this.storeCodeVerifier(codeVerifier)

    const params = await this.buildAuthorizeParams(codeVerifier)

    return SecureEndpoint().post('/api/v1/oauth2/token/qr', {
      ...params,
    })
  }
}

export default new OAuthService('72fc5f15-6432-57f2-9884-21ead18dae4c')
